import { PaginatedEnvelope, ServiceResult } from "./api";

import { TrainingMethod, type Exercise } from "./exercise";
import { Challenge } from "./challenge";
export type { Exercise } from "./exercise";

// Training program type
export enum TrainingProgramType {
  CARDIO = "cardio",
  MOBILITY_AND_WARM_UP = "mobility_and_warm_up",
  HYPERTROPHY = "hypertrophy",
  HYBRID = "hybrid",
  STRENGTH = "strength",
  OTHER = "other",
}

// Difficulty level
export enum DifficultyLevel {
  BEGINNER = "beginner",
  INTERMEDIATE = "intermediate",
  ADVANCED = "advanced",
}

// Workout duration
export enum WorkoutDuration {
  FIFTEEN = "15 mins",
  FIFTEEN_TO_THIRTY = "15-30 mins",
  THIRTY_TO_FOURTY_FIVE = "30-45 mins",
  FOURTY_FIVE = "45 mins",
  FOURTY_FIVE_TO_SIXTY = "45-60 mins",
  SIXTY = "60 mins",
  SIXTY_TO_SEVENTY_FIVE = "60-75 mins",
  SIXTY_TO_NINETY = "60-90 mins",
  SEVENTY_FIVE_TO_NINETY = "75-90 mins",
  NINETY = "90 mins",
  NINETY_TO_ONE_TWENTY = "90-120 mins",
}

// Program image
export interface ProgramImage {
  id: number;
  original_name: string;
  storage_name: string;
  path: string;
  fileable_type: string;
  fileable_id: number;
  type: string;
  full_path: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

// Training Program interface
export interface TrainingProgram {
  id: number;
  name: string;
  program_type: string;
  difficulty_level: string[];
  duration_weeks: number;
  training_days_per_week?: number;
  start_date?: string;
  end_date?: string;
  parent_program_id?: number | null;
  workout_duration: string;
  created_by_id: number | null;
  is_admin: boolean;
  is_visible?: boolean;
  description: string | null;
  equipment?: string[] | null;
  image?: string | null;
  training_program_image?: ProgramImage | null;
  created_at: string;
  updated_at: string;
  created_by: any | null;
  users?: any[];
  exercises?: Exercise[];
  training_weeks?: TrainingWeek[];
  similarity_score?: string;
  challenges?: Challenge[];
}

// Resistance band level constants
export const ResistanceBandLevel = {
  EXTRA_LIGHT: 5,
  LIGHT: 11,
  MEDIUM: 20,
  HEAVY: 27,
  EXTRA_HEAVY: 41,
} as const;

// Type for resistance band level
export type ResistanceBandLevelValue =
  (typeof ResistanceBandLevel)[keyof typeof ResistanceBandLevel];

// Weight Set interface
export interface WeightSetData {
  reps: number;
  weight?: number | null;
  completed: boolean;
}

// Resistance Band Set interface
export interface ResistanceBandSetData {
  reps: number;
  resistance_level: number;
  completed: boolean;
}

// Timed Set interface
export interface TimedSetData {
  duration_seconds: number;
  completed: boolean;
}

// Type for all set data types
export type SetData = WeightSetData | ResistanceBandSetData | TimedSetData;

// User exercise activity log interface
export interface UserExerciseActivityLog {
  id: number;
  user_id: number;
  training_day_id: number;
  exercise_id: number;
  notes?: string | null;
  sets_data: Record<string, SetData>;
  position: number;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
  exercise: {
    id: number;
    name: string;
    is_at_home: boolean;
    is_gym_based: boolean;
    training_method: TrainingMethod;
    type: string;
    yt_link?: string | null;
    target_muscle_group?: string | null;
    description?: string | null;
    admin_note?: string | null;
    created_at: string;
    updated_at: string;
  };
}

// Training Day interface
export interface TrainingDay {
  id: number;
  training_week_id: number;
  day_of_the_week: number;
  scheduled_date: string;
  is_rest_day: boolean;
  created_at: string;
  updated_at: string;
}

// Training Day Detail interface (for single day API response)
export interface TrainingDayDetail extends TrainingDay {
  training_week: {
    id: number;
    program_id: number;
    sequence_number: number;
    start_date: string;
    end_date: string;
    created_at: string;
    updated_at: string;
  };
  user_exercise_activity_logs: UserExerciseActivityLog[];
}

// Training Day Detail API Response
export interface TrainingDayDetailApiResponse {
  data: TrainingDayDetail;
}

// Training Week interface
export interface TrainingWeek {
  id: number;
  program_id: number;
  sequence_number: number;
  start_date: string;
  end_date: string;
  created_at: string;
  updated_at: string;
  training_days: TrainingDay[];
}

// Training Weeks API Response structure
export type TrainingWeeksApiResponse = PaginatedEnvelope<TrainingWeek>;

// Create program input interface (for forms)
export interface CreateProgramInput {
  name: string;
  program_type: string;
  difficulty_level: string[];
  duration_weeks: number;
  training_days_per_week?: number;
  workout_duration: string;
  description?: string;
  image?: string;
  is_visible?: boolean;
}

// Update program input interface (for editing)
export interface UpdateProgramInput {
  name?: string;
  program_type?: string;
  difficulty_level?: string[];
  duration_weeks?: number;
  training_days_per_week?: number;
  workout_duration?: string;
  description?: string;
  equipment?: string[] | null;
  image?: string;
  is_visible?: boolean;
}

// Program filters for searching/filtering
export interface ProgramFilters {
  search?: string;
  program_type?: string;
  difficulty_level?: string[];
  challengeId?: number;
}

// Laravel pagination response structure for programs
export type ProgramsApiResponse = PaginatedEnvelope<TrainingProgram>;

// Programs response for hook
export interface ProgramsResponse {
  programs: TrainingProgram[];
  total: number;
  page: number;
  limit: number;
}

export type ProgramOperationResult = ServiceResult<TrainingProgram>;
